import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  FileText, DollarSign, MessageSquare, Menu, X,
  ChevronRight, HelpCircle, Headphones, Users, Bell, LogOut,
} from "lucide-react";
import { HomeLensLogo } from "@/components/HomeLensLogo";
import { toast } from "sonner";

const NAV_ITEMS = [
  { id: "transactions", label: "Transactions", icon: FileText, path: "/dashboard" },
  { id: "documents", label: "Documents", icon: FileText, tab: "documents" },
  { id: "offers", label: "Offers", icon: DollarSign, tab: "offers" },
  { id: "messages", label: "Messages", icon: MessageSquare, tab: "chat" },
];

const TEAM_ITEMS = [
  { id: "team-directory", label: "Team Directory", icon: Users, tab: "parties" },
];

const SUPPORT_ITEMS = [
  { id: "help", label: "Help & Resources", icon: HelpCircle },
  { id: "support", label: "Contact Support", icon: Headphones },
];

export const SidebarLayout = ({ children, activeNav = "dashboard", onTabNavigate, transactionId, transactionRole }) => {
  const navigate = useNavigate();
  const { displayName, displayRole, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Use transaction-specific role if available, otherwise fall back to global profile role
  const effectiveRole = transactionRole || displayRole;

  const handleNavClick = (item) => {
    setMobileOpen(false);

    // Direct path navigation (Dashboard, Transactions)
    if (item.path) {
      navigate(item.path);
      return;
    }

    // Tab-based navigation (Documents, Offers, Messages, Team Directory)
    if (item.tab) {
      if (onTabNavigate && transactionId) {
        // Already on a transaction page — switch tab
        onTabNavigate(item.tab);
      } else if (transactionId) {
        // Has transactionId but no callback — navigate to transaction
        navigate(`/transaction/${transactionId}`);
      } else {
        // On dashboard — go to dashboard (user picks a transaction first)
        toast.info(`Select a transaction to view ${item.label}`);
      }
      return;
    }

    // Support items
    if (item.id === "help") {
      toast.info("Help & Resources — coming soon");
    } else if (item.id === "support") {
      toast.info("Contact Support — coming soon");
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch (e) { console.error(e); }
    navigate("/");
    toast.success("Logged out");
  };

  const initials = displayName
    ? displayName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "U";

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const isActive = activeNav === item.id;
    return (
      <button
        key={item.id}
        onClick={() => handleNavClick(item)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? "bg-[#2E4A7F] text-white border-l-[3px] border-blue-400 -ml-0.5 pl-[14px]"
            : "text-[#AAB7D1] hover:bg-[#2E4A7F]/30 hover:text-white"
        }`}
        data-testid={`sidebar-link-${item.id}`}
      >
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
        {item.label}
      </button>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col justify-between h-full" data-testid="sidebar">
      <div>
        {/* Logo */}
        <div className="px-5 pt-6 pb-8">
          <HomeLensLogo className="text-white [&_svg]:text-white [&_span]:text-white" />
        </div>

        {/* Primary Nav */}
        <nav className="space-y-0.5 px-3">
          {NAV_ITEMS.map((item) => <NavItem key={item.id} item={item} />)}
        </nav>

        {/* Team Section */}
        <div className="mt-8 px-3">
          <p className="px-3 text-[10px] font-semibold tracking-widest uppercase text-[#AAB7D1]/50 mb-2">Your Team</p>
          {TEAM_ITEMS.map((item) => <NavItem key={item.id} item={item} />)}
        </div>

        {/* Support Section */}
        <div className="mt-6 px-3">
          <p className="px-3 text-[10px] font-semibold tracking-widest uppercase text-[#AAB7D1]/50 mb-2">Support</p>
          {SUPPORT_ITEMS.map((item) => <NavItem key={item.id} item={item} />)}
        </div>
      </div>

      {/* Bottom — User Profile */}
      <div className="px-3 pb-5">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-md hover:bg-[#2E4A7F]/30 transition-colors group"
          data-testid="sidebar-user-profile"
        >
          <Avatar className="h-9 w-9 shrink-0">
            <AvatarFallback className="bg-[#2E4A7F] text-white text-xs font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-medium text-white truncate">{displayName}</p>
            <p className="text-xs text-[#AAB7D1] capitalize">{effectiveRole}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-[#AAB7D1] group-hover:text-white transition-colors shrink-0" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-[260px] bg-[#1A2B4F] z-40" data-testid="sidebar-desktop">
        <SidebarContent />
      </aside>

      {/* Mobile Hamburger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-100 h-14 flex items-center justify-between px-4">
        <HomeLensLogo size="sm" />
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-500">
            <Bell className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setMobileOpen(!mobileOpen)} data-testid="sidebar-mobile-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-[280px] bg-[#1A2B4F] z-50" data-testid="sidebar-mobile">
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Top bar for desktop */}
      <div className="hidden lg:flex fixed top-0 left-[260px] right-0 h-14 bg-white border-b border-slate-100 z-30 items-center justify-end px-8">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-slate-600">
            <Bell className="w-5 h-5" strokeWidth={1.75} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Avatar className="h-9 w-9 cursor-pointer" data-testid="topbar-avatar">
                <AvatarFallback className="bg-[#1A2B4F] text-white text-xs font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 shadow-soft" data-testid="topbar-dropdown">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-slate-900">{displayName}</p>
                <p className="text-xs text-slate-400 capitalize">{effectiveRole}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={handleLogout} className="text-red-600 focus:text-red-600" data-testid="topbar-signout">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <main className="lg:ml-[260px] pt-14 min-h-screen bg-white">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
